import { Timestamp, serverTimestamp, type FieldValue } from "firebase/firestore";
import { dateValue } from "./firestore";
import { preferredName, type SBotUser } from "./user";

export type ActionAuditActionType = "create" | "update" | "archive" | "undo";
export type ActionAuditActorKind = "human" | "aiAssistant" | "externalAgent";

export interface ActionAuditActor {
  userID: string;
  displayName: string;
  kind: ActionAuditActorKind;
  toolActionType?: string;
}

export type ActionAuditValue =
  | { kind: "string"; value: string }
  | { kind: "int"; value: number }
  | { kind: "double"; value: number }
  | { kind: "bool"; value: boolean }
  | { kind: "timestamp"; value: Date }
  | { kind: "null" }
  | { kind: "json"; value: string };

export interface ActionAuditFieldChange {
  key: string;
  displayLabel?: string;
  oldValue?: ActionAuditValue;
  newValue?: ActionAuditValue;
  isTruncated: boolean;
}

export interface ActionAuditEvent {
  id: string;
  leagueID: string;
  organizationID?: string;
  subjectUserID?: string;
  targetCollection: string;
  targetDocumentID: string;
  targetDisplayName: string;
  actionType: ActionAuditActionType;
  actorUserID: string;
  actorDisplayName: string;
  actorKind: ActionAuditActorKind;
  toolActionType?: string;
  fieldChanges: ActionAuditFieldChange[];
  reversesEventID?: string;
  reversesFieldKey?: string;
  clientTimestamp: Date;
  createdAt?: Date;
  expiresAt: Date;
}

export type ActionAuditScope =
  | { kind: "league"; leagueID: string }
  | { kind: "organizations"; leagueID: string; organizationIDs: string[] }
  | { kind: "athleteSelf"; userID: string };

export interface ActionAuditContext {
  leagueID: string;
  organizationID?: string;
  subjectUserID?: string;
  targetCollection: string;
  targetDocumentID: string;
  targetDisplayName: string;
  actionType: ActionAuditActionType;
  actor: ActionAuditActor;
  fieldLabels?: Record<string, string>;
  reversesEventID?: string;
  reversesFieldKey?: string;
}

export const ACTION_AUDIT_SCHEMA_VERSION = 1;
export const ACTION_AUDIT_JSON_MARKER_KEY = "_sbotAuditJSON";
export const ACTION_AUDIT_OVERFLOW_SENTINEL_KEY = "_truncatedChangeCount";
export const ACTION_AUDIT_WINDOW_MS = 24 * 60 * 60 * 1_000;
export const ACTION_AUDIT_MAX_CHANGES = 100;
export const ACTION_AUDIT_MAX_VALUE_LENGTH = 4_096;
export const ACTION_AUDIT_IGNORED_KEYS = new Set([
  "id",
  "createdAt",
  "updatedAt",
  "createdByUserID",
  "updatedByUserID",
]);

const ACTION_TYPES: ActionAuditActionType[] = ["create", "update", "archive", "undo"];
const ACTOR_KINDS: ActionAuditActorKind[] = ["human", "aiAssistant", "externalAgent"];

export function humanActionAuditActor(user: SBotUser): ActionAuditActor {
  return { userID: user.id, displayName: preferredName(user), kind: "human" };
}

export function aiAssistantActionAuditActor(user: SBotUser, toolActionType: string): ActionAuditActor {
  return {
    userID: user.id,
    displayName: preferredName(user),
    kind: "aiAssistant",
    toolActionType,
  };
}

/**
 * Writes performed on the user's behalf by an agent they brought themselves
 * (WebMCP bridge). Distinct from the in-app assistant so the activity trail
 * shows who was driving.
 */
export function externalAgentActionAuditActor(user: SBotUser, toolActionType: string): ActionAuditActor {
  return {
    userID: user.id,
    displayName: preferredName(user),
    kind: "externalAgent",
    toolActionType,
  };
}

export function actionAuditDocumentID(actorUserID: string, seed = crypto.randomUUID().toLowerCase()): string {
  return `${actorUserID}_${seed}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function isFlattenableRecord(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && !(value instanceof Date) && !(value instanceof Timestamp);
}

export function actionAuditValueFromFirestore(value: unknown): ActionAuditValue | undefined {
  if (
    isRecord(value)
    && Object.keys(value).length === 1
    && typeof value[ACTION_AUDIT_JSON_MARKER_KEY] === "string"
  ) {
    return { kind: "json", value: value[ACTION_AUDIT_JSON_MARKER_KEY] };
  }
  if (value === undefined) return undefined;
  if (value === null) return { kind: "null" };
  if (typeof value === "boolean") return { kind: "bool", value };
  if (typeof value === "number") {
    return Number.isInteger(value) ? { kind: "int", value } : { kind: "double", value };
  }
  if (typeof value === "string") return { kind: "string", value };
  const date = dateValue(value);
  if (date) return { kind: "timestamp", value: date };
  try {
    return { kind: "json", value: JSON.stringify({ v: value }) };
  } catch {
    return undefined;
  }
}

export function actionAuditValueFirestoreData(value: ActionAuditValue): unknown {
  switch (value.kind) {
    case "string":
    case "int":
    case "double":
    case "bool":
      return value.value;
    case "timestamp":
      return Timestamp.fromDate(value.value);
    case "null":
      return null;
    case "json":
      return { [ACTION_AUDIT_JSON_MARKER_KEY]: value.value };
  }
}

export function isActionAuditValueRestorable(value: ActionAuditValue): boolean {
  return value.kind !== "json";
}

export function isActionAuditFieldUndoable(change: ActionAuditFieldChange): boolean {
  return !change.isTruncated
    && change.key !== ACTION_AUDIT_OVERFLOW_SENTINEL_KEY
    && (!change.oldValue || isActionAuditValueRestorable(change.oldValue));
}

export function actionAuditFieldChangeFirestoreData(change: ActionAuditFieldChange): Record<string, unknown> {
  const data: Record<string, unknown> = { key: change.key };
  if (change.displayLabel !== undefined) data.displayLabel = change.displayLabel;
  if (change.oldValue !== undefined) data.oldValue = actionAuditValueFirestoreData(change.oldValue);
  if (change.newValue !== undefined) data.newValue = actionAuditValueFirestoreData(change.newValue);
  if (change.isTruncated) data.isTruncated = true;
  return data;
}

export function parseActionAuditFieldChange(data: unknown): ActionAuditFieldChange | null {
  if (!isRecord(data) || typeof data.key !== "string" || !data.key) return null;
  return {
    key: data.key,
    displayLabel: typeof data.displayLabel === "string" ? data.displayLabel : undefined,
    oldValue: Object.hasOwn(data, "oldValue") ? actionAuditValueFromFirestore(data.oldValue) : undefined,
    newValue: Object.hasOwn(data, "newValue") ? actionAuditValueFromFirestore(data.newValue) : undefined,
    isTruncated: data.isTruncated === true,
  };
}

export function actionAuditEventFirestoreData(event: ActionAuditEvent): Record<string, unknown | FieldValue> {
  const data: Record<string, unknown | FieldValue> = {
    id: event.id,
    schemaVersion: ACTION_AUDIT_SCHEMA_VERSION,
    leagueID: event.leagueID,
    targetCollection: event.targetCollection,
    targetDocumentID: event.targetDocumentID,
    targetDisplayName: event.targetDisplayName,
    actionType: event.actionType,
    actorUserID: event.actorUserID,
    actorDisplayName: event.actorDisplayName,
    actorKind: event.actorKind,
    fieldChanges: event.fieldChanges.map(actionAuditFieldChangeFirestoreData),
    clientTimestamp: Timestamp.fromDate(event.clientTimestamp),
    createdAt: serverTimestamp(),
    expiresAt: Timestamp.fromDate(event.expiresAt),
  };
  if (event.organizationID !== undefined) data.organizationID = event.organizationID;
  if (event.subjectUserID !== undefined) data.subjectUserID = event.subjectUserID;
  if (event.toolActionType !== undefined) data.toolActionType = event.toolActionType;
  if (event.reversesEventID !== undefined) data.reversesEventID = event.reversesEventID;
  if (event.reversesFieldKey !== undefined) data.reversesFieldKey = event.reversesFieldKey;
  return data;
}

export function parseActionAuditEvent(id: string, data: Record<string, unknown>): ActionAuditEvent | null {
  const clientTimestamp = dateValue(data.clientTimestamp);
  const expiresAt = dateValue(data.expiresAt);
  if (
    data.id !== id
    || typeof data.leagueID !== "string"
    || !data.leagueID
    || typeof data.targetCollection !== "string"
    || !data.targetCollection
    || typeof data.targetDocumentID !== "string"
    || !data.targetDocumentID
    || !ACTION_TYPES.includes(data.actionType as ActionAuditActionType)
    || typeof data.actorUserID !== "string"
    || !ACTOR_KINDS.includes(data.actorKind as ActionAuditActorKind)
    || !clientTimestamp
    || !expiresAt
  ) {
    return null;
  }
  return {
    id,
    leagueID: data.leagueID,
    organizationID: typeof data.organizationID === "string" ? data.organizationID : undefined,
    subjectUserID: typeof data.subjectUserID === "string" ? data.subjectUserID : undefined,
    targetCollection: data.targetCollection,
    targetDocumentID: data.targetDocumentID,
    targetDisplayName: typeof data.targetDisplayName === "string" ? data.targetDisplayName : "",
    actionType: data.actionType as ActionAuditActionType,
    actorUserID: data.actorUserID,
    actorDisplayName: typeof data.actorDisplayName === "string" ? data.actorDisplayName : "",
    actorKind: data.actorKind as ActionAuditActorKind,
    toolActionType: typeof data.toolActionType === "string" ? data.toolActionType : undefined,
    fieldChanges: (Array.isArray(data.fieldChanges) ? data.fieldChanges : [])
      .map(parseActionAuditFieldChange)
      .filter((change): change is ActionAuditFieldChange => change !== null),
    reversesEventID: typeof data.reversesEventID === "string" ? data.reversesEventID : undefined,
    reversesFieldKey: typeof data.reversesFieldKey === "string" ? data.reversesFieldKey : undefined,
    clientTimestamp,
    createdAt: dateValue(data.createdAt),
    expiresAt,
  };
}

export function flattenActionAuditData(
  data: Record<string, unknown>,
  prefix = "",
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (isFlattenableRecord(value) && Object.keys(value).length > 0) {
      Object.assign(result, flattenActionAuditData(value, path));
    } else {
      result[path] = value;
    }
  }
  return result;
}

function equalFirestoreValues(first: unknown, second: unknown): boolean {
  if (first === undefined || second === undefined) return first === second;
  const firstDate = dateValue(first);
  const secondDate = dateValue(second);
  if (firstDate && secondDate) return Math.abs(firstDate.getTime() - secondDate.getTime()) < 1;
  if (Object.is(first, second)) return true;
  if (Array.isArray(first) && Array.isArray(second)) {
    return first.length === second.length && first.every((value, index) => equalFirestoreValues(value, second[index]));
  }
  if (isRecord(first) && isRecord(second)) {
    const firstKeys = Object.keys(first).sort();
    const secondKeys = Object.keys(second).sort();
    return firstKeys.length === secondKeys.length
      && firstKeys.every((key, index) => key === secondKeys[index] && equalFirestoreValues(first[key], second[key]));
  }
  return false;
}

export const isEqualActionAuditFirestoreValue = equalFirestoreValues;

function boundedValue(value: ActionAuditValue | undefined): {
  value: ActionAuditValue | undefined;
  truncated: boolean;
} {
  if ((value?.kind === "string" || value?.kind === "json") && value.value.length > ACTION_AUDIT_MAX_VALUE_LENGTH) {
    return { value: { ...value, value: value.value.slice(0, ACTION_AUDIT_MAX_VALUE_LENGTH) }, truncated: true };
  }
  return { value, truncated: false };
}

export function actionAuditFieldChanges(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown>,
  fieldLabels: Record<string, string> = {},
): ActionAuditFieldChange[] {
  const flatBefore = before ? flattenActionAuditData(before) : {};
  const flatAfter = flattenActionAuditData(after);
  const candidateKeys = [...new Set([...Object.keys(flatBefore), ...Object.keys(flatAfter)])]
    .filter((key) => !ACTION_AUDIT_IGNORED_KEYS.has(key.split(".")[0] ?? key))
    .sort();
  let changes = candidateKeys.flatMap((key): ActionAuditFieldChange[] => {
    const oldRaw = flatBefore[key];
    const newRaw = flatAfter[key];
    if (equalFirestoreValues(oldRaw, newRaw)) return [];
    const oldValue = boundedValue(actionAuditValueFromFirestore(oldRaw));
    const newValue = boundedValue(actionAuditValueFromFirestore(newRaw));
    return [{
      key,
      displayLabel: fieldLabels[key],
      oldValue: oldValue.value,
      newValue: newValue.value,
      isTruncated: oldValue.truncated || newValue.truncated,
    }];
  });
  if (changes.length > ACTION_AUDIT_MAX_CHANGES) {
    const overflow = changes.length - ACTION_AUDIT_MAX_CHANGES;
    changes = changes.slice(0, ACTION_AUDIT_MAX_CHANGES);
    changes.push({
      key: ACTION_AUDIT_OVERFLOW_SENTINEL_KEY,
      displayLabel: "Additional changes",
      newValue: { kind: "int", value: overflow },
      isTruncated: true,
    });
  }
  return changes;
}

export function actionAuditReferenceDate(event: ActionAuditEvent): Date {
  return event.createdAt ?? event.clientTimestamp;
}

export function actionAuditEventIsExpired(event: ActionAuditEvent, now = new Date()): boolean {
  return event.expiresAt.getTime() <= now.getTime();
}

export function actionAuditValueDisplayString(value?: ActionAuditValue): string {
  if (!value || value.kind === "null") return "—";
  switch (value.kind) {
    case "string":
      return value.value || "—";
    case "int":
      return String(value.value);
    case "double":
      return new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 }).format(value.value);
    case "bool":
      return value.value ? "Yes" : "No";
    case "timestamp":
      return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(value.value);
    case "json":
      return value.value;
  }
}

export function actionAuditFieldDisplayName(key: string, event: ActionAuditEvent): string {
  return event.fieldChanges.find((change) => change.key === key)?.displayLabel
    ?? key.split(".").at(-1)
    ?? key;
}

export function actionAuditEventSummary(event: ActionAuditEvent): string {
  const target = event.targetDisplayName || event.targetCollection;
  if (event.actionType === "create") return `${event.actorDisplayName} created ${target}`;
  if (event.actionType === "archive") return `${event.actorDisplayName} archived ${target}`;
  if (event.actionType === "undo") {
    const field = event.reversesFieldKey ? actionAuditFieldDisplayName(event.reversesFieldKey, event) : "a change";
    return `${event.actorDisplayName} restored ${field} for ${target}`;
  }
  const fields = event.fieldChanges.filter((change) => change.key !== ACTION_AUDIT_OVERFLOW_SENTINEL_KEY);
  const first = fields[0];
  if (!first) return `${event.actorDisplayName} updated ${target}`;
  const firstName = first.displayLabel ?? first.key.split(".").at(-1) ?? first.key;
  return fields.length === 1
    ? `${event.actorDisplayName} updated ${firstName} for ${target}`
    : `${event.actorDisplayName} updated ${firstName} and ${fields.length - 1} more for ${target}`;
}

export function actionAuditBucketTitle(date: Date, now = new Date()): "Last hour" | "Last 6 hours" | "Earlier" {
  const age = now.getTime() - date.getTime();
  if (age < 60 * 60 * 1_000) return "Last hour";
  if (age < 6 * 60 * 60 * 1_000) return "Last 6 hours";
  return "Earlier";
}

export function groupActionAuditEvents(
  events: ActionAuditEvent[],
  now = new Date(),
): Array<{ title: string; events: ActionAuditEvent[] }> {
  const groups: Array<{ title: string; events: ActionAuditEvent[] }> = [];
  for (const event of events) {
    const title = actionAuditBucketTitle(actionAuditReferenceDate(event), now);
    const existing = groups.find((group) => group.title === title);
    if (existing) existing.events.push(event);
    else groups.push({ title, events: [event] });
  }
  return groups;
}
